import { Position, PositionType, ToolType } from '../../../shared/types/protocol.ts';
import { getToleranceRangeStrings } from '../../../shared/utils/getToleranceRange.ts';
import { isOutOfSpecValue } from '../../../shared/utils/isOutOfSpec.ts';
import { sortBy } from '../../../shared/utils/sortUtils.ts';
import { readCmmFile } from '../../services/cmm-import/readCmmFile.ts';
import { CmmFileRead } from '../../services/cmm-import/types.ts';
import { CmmConfig, CustomEquipment, Protocol } from '../../types/sharedTypeImpl.ts';
import { TestImportFile, TestImportPosition } from './types.ts';

export async function testImportCmmFiles(
    protocol: Protocol,
    cmmConfigs: CmmConfig[],
    cmmEquipments: CustomEquipment[] | undefined,
    files: File[],
): Promise<TestImportFile> {
    // const cmmReadResults = await Promise.all(files.map(readCmmFile));
    const cmmReadResults = await Promise.all(files.map((file) => readCmmFile(file, cmmConfigs, undefined, cmmEquipments ?? [])));
    const readErrors = cmmReadResults.filter((it) => !it.success).map((it) => it.errorMessage);
    if (readErrors.length > 0) {
        return {
            success: false,
            errorMessage: readErrors[0],
        };
    }
    const positionNumberCounts = countPositionNumbers(cmmReadResults);
    const positionResults = checkPositions(cmmReadResults, protocol, positionNumberCounts);

    sortBy(positionResults, (it) => +it.positionNumber);
    return {
        success: true,
        positions: positionResults,
    };
}


function countPositionNumbers(cmmReadResults: CmmFileRead[]): Map<string, number> {
    const positionNumberCounts = new Map<string, number>();
    for (const cmmReadResult of cmmReadResults) {
        for (const position of cmmReadResult.positions!) {
            positionNumberCounts.set(
                position.positionNumber,
                (positionNumberCounts.get(position.positionNumber) ?? 0) + 1
            );
        }
    }
    return positionNumberCounts;
}

function checkPositions(
    cmmReadResults: CmmFileRead[],
    protocol: Protocol,
    positionNumberCounts: Map<string, number>
): TestImportPosition[] {
    // Check positions in CMM file
    const isMissing = (v:any) => v == null || v === '';
    const positionResults: TestImportPosition[] = [];
    for (const cmmReadResult of cmmReadResults) {
        for (const cmmPosition of cmmReadResult.positions!) {
            const parsedImportedValue = parseFloat(cmmPosition.value);
            const protocolPosition = protocol.positions.find(it => it.positionNumber === cmmPosition.positionNumber);
            console.log(`protocolPosition: `, protocolPosition)
            const issues: string[] = [];
            if (cmmPosition.issue) {
                issues.push(cmmPosition.issue);
            } else if (!protocolPosition) {
                issues.push('Position not in protocol');
            } else if (protocolPosition.type !== 'measurement') {
                issues.push(`Position is ${getPositionTypeLabel(protocolPosition)} in protocol, not measurement`);
            } else if (protocolPosition.type === 'measurement' &&
                    (   isMissing(protocolPosition.nominal) ||
                        isMissing(protocolPosition.lowerTolerance) ||
                        isMissing(protocolPosition.upperTolerance)
                    )
                ) {
                issues.push('No nominal/tolerance defined in protocol');
            } else if (isOutOfSpecValue(protocolPosition, parsedImportedValue)) {
                const { min: minString, max: maxString } = getToleranceRangeStrings(protocolPosition);
                issues.push(`Out of tolerance: ${minString}...${maxString}`);
            }
            if (positionNumberCounts.get(cmmPosition.positionNumber)! > 1) {
                issues.push('Duplicate position');
            }

            positionResults.push({
                positionNumber: cmmPosition.positionNumber,
                positionType: getPositionType(protocolPosition),
                value: cmmPosition.value,
                isOk: issues.length === 0,
                issues,
            });
        }
    }

    // Check for missing positions
    for (const position of protocol.positions) {
        if (positionNumberCounts.get(position.positionNumber)) continue

        const isIssue = position.type === 'measurement'
            && (position.toolType === 'CMM' || position.toolType === 'VMM');

        positionResults.push({
            positionNumber: position.positionNumber,
            positionType: getPositionType(position),
            value: '',
            isOk: !isIssue,
            issues: isIssue ? ['Position not in CMM file'] : [],
        });
    }

    return positionResults;
}

function getPositionType(
    position: Position | undefined
): PositionType | ToolType | undefined {
    if (!position) return undefined;
    return position.type === 'visualInspection'
        ? position.type
        : position.toolType;
}

function getPositionTypeLabel(position: Position): string {
    switch (position.type) {
        case 'generalInspection':
            return 'general inspection';
        case 'visualInspection':
            return 'visual inspection';
        default:
            return position.type;
    }
}
